import React, { useRef, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { PersistGate } from "redux-persist/integration/react";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { ThemeProvider } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Routes from "./Routes";
import store from "./config/configureStore";
import { history } from "./config/configureStore";
import ScrollToTop from "./utils/ScrollToTop";
import { DayTheme, NightTheme } from "./theme";
import Notifier from "./global-components/Notifier";
import { AnimatePresence } from "framer-motion";
import Spinner from "./components/Spinner";
// import NotificationPanel from "./components/NotificationPanel";
import "./assets/base.scss";
import "./icons";
import { notify } from "./reducers/NotifierReducer";
import { getSettings } from "./reducers/ConfigReducer";
import * as serviceWorker from "./serviceWorker";
import { getDictionaries, getUsers } from "./reducers/DictionaryReducer";
import Dialogs from "./components/Dialogs";
import { getUserPermissions } from "./reducers/PermissionsReducer";
import { getScreensView } from "./reducers/UiReducer";

LicenseInfo.setLicenseKey(window.__RUNTIME_CONFIG__.REACT_APP_XGRID_KEY);

const App = (props) => {
  const snackbarRef = useRef();
  const dispatch = useDispatch();
  const {
    authenticated,
    websocket,
    ui,
    getDictionaries,
    getUserPermissions,
    getUsers,
    userData,
  } = props;
  const { showSpinner } = ui;
  const themeMode = () => {
    let theme = DayTheme;
    if (props.mode === "dark") {
      theme = NightTheme;
    }
    return theme;
  };

  const updateApp = (registration) => {
    window.localStorage.clear();
    const waitingServiceWorker = registration.waiting;

    if (waitingServiceWorker) {
      waitingServiceWorker.addEventListener("stateChange", (event) => {
        if (event.target.state === "activated") {
          window.location.href = "/home";
        }
      });
      waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
    }
  };

  useEffect(() => {
    const openNotification = (registration) => {
      const notification = {
        title: `Application Update Available!`,
        message: `New release of AMS Application has been released. It is highly recommended to launch the new version!`,
        position: "tc",
        level: "warning",
        autoDismiss: 0,
        action: {
          label: "LAUNCH!",
          callback: () => updateApp(registration),
        },
      };
      props.notifyPanel(notification, "success");
    };

    serviceWorker.register({
      onSuccess: () => console.log("service worker registered!"),
      onUpdate: (reg) => openNotification(reg),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (websocket.websocket && authenticated) {
      props.getSettings();
      getDictionaries("ams");
      getUsers();
      getUserPermissions();
      if (userData?.user?.ptsUserID) dispatch(getScreensView(userData?.user?.ptsUserID))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocket, authenticated]);

  const closeSnackbar = (key) => () => {
    snackbarRef.current.closeSnackbar(key);
  };

  return (
    <ConnectedRouter history={history}>
      <PersistGate loading={null} persistor={store.persistor}>
        <CssBaseline />
        <ScrollToTop>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ThemeProvider theme={themeMode()}>
              <SnackbarProvider
                ref={snackbarRef}
                action={(key) => (
                  <IconButton onClick={closeSnackbar(key)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <Notifier />
                {/* <NotificationPanel /> */}
                <AnimatePresence>
                  <Routes />
                </AnimatePresence>
              </SnackbarProvider>
              <Dialogs />
            </ThemeProvider>
          </LocalizationProvider>
        </ScrollToTop>
      </PersistGate>
      {showSpinner && <Spinner />}
    </ConnectedRouter>
  );
};

const mapStateToProps = (state) => ({
  mode: state.theme.mode,
  ui: state.ui,
  websocket: state.websocket,
  authenticated: state.user.isAuthenticated,
  userData: state.user.userData,
  stateTheme: state.theme,
});

export default connect(mapStateToProps, {
  notify,
  getSettings,
  getDictionaries,
  getUsers,
  getUserPermissions,
})(App);
